// client/src/components/stats/RfiBreakdown.tsx
//
// Muestra el breakdown de RFI (Raise First In) por posición.
// A diferencia de MatchupBreakdown, siempre mostramos las 5 posiciones
// (EP, HJ, CO, BTN, SB) aunque alguna tenga opp=0 — la grilla completa es
// más informativa que filas faltantes.

import React from 'react';
import { RfiDerived } from '../../utils/pokerStats/types';
import { MIN_RFI_SAMPLE } from '../../utils/pokerStats/constants';
import { useT } from '../../lib/i18n';
import { StackKey } from './leakRanges';
import { useBenchmarks } from '../../contexts/BenchmarkContext';

interface RfiBreakdownProps {
  rfi: RfiDerived[];
  activeStack: StackKey;
  label?: string;
}

export function RfiBreakdown({ rfi, activeStack, label }: RfiBreakdownProps) {
  const { t } = useT();
  const bm = useBenchmarks();

  const totalOpp = rfi.reduce((s, r) => s + r.opp, 0);

  if (totalOpp === 0) {
    return (
      <div className="rfi-breakdown rfi-empty">
        <p className="rfi-empty-text">{t('stats.rfi.empty')}</p>
      </div>
    );
  }

  // ¿Hay al menos una posición con sample suficiente?
  const anySufficient = rfi.some(r => !r.lowSample && r.opp > 0);

  return (
    <div className="rfi-breakdown">
      {label && <div className="rfi-breakdown-title">{label}</div>}

      {!anySufficient && (
        <div className="rfi-warning">{t('stats.rfi.warning', { n: MIN_RFI_SAMPLE })}</div>
      )}

      <table className="rfi-table">
        <thead>
          <tr>
            <th>{t('stats.rfi.thPosition')}</th>
            <th className="rfi-th-num">{t('stats.rfi.thRfi')}</th>
            <th className="rfi-th-num">{t('stats.rfi.thBenchmark')}</th>
            <th className="rfi-th-num">{t('stats.rfi.thLimp')}</th>
            <th className="rfi-th-num">{t('stats.rfi.thSample')}</th>
            <th className="rfi-th-status" />
          </tr>
        </thead>
        <tbody>
          {rfi.map(r => {
            const benchmark  = bm.getRfiBenchmark(r.position, activeStack);
            const rfiStatus  = r.lowSample || r.opp === 0 ? 'ok' : bm.evalRfi(r.position, r.pct, activeStack);
            const limpStatus = r.lowSample || r.opp === 0 ? 'ok' : bm.evalStat('openLimp', r.limpPct, activeStack);
            const showEval   = !r.lowSample && r.opp > 0;

            return (
              <tr
                key={r.position}
                className={`rfi-row rfi-row-${rfiStatus} ${r.lowSample ? 'rfi-row-low' : ''}`}
              >
                <td className="rfi-cell-pos">{r.position}</td>
                <td className="rfi-cell-num rfi-cell-value">
                  {r.opp > 0 ? `${r.pct}%` : '—'}
                </td>
                <td className="rfi-cell-num rfi-cell-benchmark">
                  {benchmark ? `${benchmark.min}–${benchmark.max}%` : '—'}
                </td>
                <td className="rfi-cell-num rfi-cell-limp">
                  {r.opp > 0 && r.limps > 0 ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', justifyContent: 'flex-end' }}>
                      {r.limpPct}%
                      {showEval && limpStatus !== 'ok' && (
                        <span className={`stat-dot dot-${limpStatus}`} style={{ flexShrink: 0 }} />
                      )}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--ok)' }}>—</span>
                  )}
                </td>
                <td className="rfi-cell-num rfi-cell-sample">
                  {r.opp > 0 ? `n=${r.opp}` : '—'}
                  {r.lowSample && r.opp > 0 && (
                    <span className="rfi-low-tag"> {t('stats.rfi.lowTag')}</span>
                  )}
                </td>
                <td className="rfi-cell-status">
                  {showEval && benchmark && <span className={`stat-dot dot-${rfiStatus}`} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
